import {t} from "@lingui/core/macro";
import {useCallback, useEffect, useRef} from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {useOnMountEffect} from "@khanacademy/wonder-blocks-core";
import {ImageSource} from "@khan/guide-types";

import {
    ALLOWED_FILE_TYPES,
    OPENAI_MAX_LONG_DIMENSION,
    OPENAI_MAX_SHORT_DIMENSION,
} from "./constants.ts";
import {maybeScaleDownImage} from "./maybe-scale-down-image.ts";

// Element ID for our rendering canvas
const CANVAS_ID = "attach-render-canvas";

interface Props {
    // A base64 encoded image to display in the preview.
    image: string | null;
    // The quality of the image to be rendered, with 0 being the
    // lowest quality and 1 being the highest quality.
    imageQuality?: number;
    // Callback function to be called when an image is attached.
    onImageAttach?: (
        dataURL: string,
        source: ImageSource.ProvidedByFile,
    ) => void;
    // File dragged in dropzone
    fileDragged?: File;
    // Set the selected filename
    setSelectedFilename: (filename: string | null) => void;
}

const FILE_INPUT_ID = "attach-input";

/**
 * The ImageAttach component allows the user to attach a single image
 * and converts it to a data URL representing a scaled-down version of the image.
 *
 * Ideally we'd use OffscreenCanvas to render the image offscreen, but it's not
 * supported in all browsers yet. So we're using a regular canvas element instead,
 * and rendering the image offscreen.
 */
export function ImageAttach({
    image,
    onImageAttach,
    fileDragged,
    setSelectedFilename,
}: Props): React.ReactElement {
    const canvasRef = useRef<HTMLCanvasElement>();

    const handleFile = useCallback(
        (file: File) => {
            const reader = new FileReader();
            reader.onerror = (error) => {
                throw new Error(`Error reading file: ${error}`);
            };
            reader.onload = (event) => {
                setSelectedFilename(file.name);
                const img = new Image();
                img.src = event.target?.result as string;
                img.onload = () => {
                    const renderCanvas = canvasRef.current;
                    if (!renderCanvas) {
                        throw new Error("Canvas element not found");
                    }

                    // Get the natural height and width of the source image
                    // so that we can preserve the aspect ratio
                    const height = img.naturalHeight;
                    const width = img.naturalWidth;
                    // No point in submitting images with no actual area.
                    if (height === 0 || width === 0) {
                        throw new Error("Image has zero height or width");
                    }

                    const {targetWidth, targetHeight} = maybeScaleDownImage(
                        width,
                        height,
                    );

                    const ctx = renderCanvas.getContext("2d");
                    if (ctx === null) {
                        throw new Error("Canvas context is null");
                    }
                    try {
                        // Set up a clean, correctly sized canvas for rendering.
                        ctx.reset();
                        renderCanvas.width = targetWidth;
                        renderCanvas.height = targetHeight;
                        // Suppress error because we know that drawImage has a
                        // signature that accepts an Image object and 4 numbers
                        // https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D/drawImage#syntax
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment -- porting from old code.
                        // @ts-ignore
                        ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
                        // extract image from canvas as dataURL
                        const extractedDataURL = (
                            renderCanvas as HTMLCanvasElement
                        ).toDataURL("image/png");
                        onImageAttach?.(
                            extractedDataURL,
                            ImageSource.ProvidedByFile,
                        );
                    } catch (err) {
                        setSelectedFilename(null);
                        throw new Error(`Error rendering image: ${err}`);
                    } finally {
                        const fileInput =
                            document.getElementById(FILE_INPUT_ID);
                        if (fileInput && "value" in fileInput) {
                            fileInput.value = "";
                        }
                    }
                };
            };
            reader.readAsDataURL(file);
        },
        [onImageAttach, setSelectedFilename],
    );

    useOnMountEffect(() => {
        const canvasEl: HTMLCanvasElement = document.getElementById(
            CANVAS_ID,
        ) as HTMLCanvasElement;
        if (!canvasEl) {
            throw new Error("Canvas element not found");
        }
        canvasRef.current = canvasEl;
    });

    useEffect(() => {
        const fileInput = document.getElementById(FILE_INPUT_ID);
        if (!fileInput) {
            throw new Error("File input element not found");
        }

        const changeListener = (e) => {
            const file = (e.target as HTMLInputElement).files?.[0];
            if (!file) {
                throw new Error("No file selected");
            }
            handleFile(file);
        };
        const cancelListener = () => {
            setSelectedFilename(null);
        };
        fileInput.addEventListener("change", changeListener);
        return () => {
            fileInput.removeEventListener("change", changeListener);
            fileInput.removeEventListener("cancel", cancelListener);
        };
    }, [onImageAttach, fileDragged, handleFile, setSelectedFilename]);

    useEffect(() => {
        if (fileDragged) {
            handleFile(fileDragged);
        }
    }, [fileDragged, handleFile]);

    const handleAttachClick = () => {
        const attachInput = document.getElementById(FILE_INPUT_ID);
        if (attachInput) {
            attachInput.click();
        }
    };

    return (
        <>
            {/* These kooky gymnastics are necessary because while we can style the */}
            {/* HTML Input button pretty well with CSS, we can't internationalize */}
            {/* the text, so we're hiding the native button and adding our own, that */}
            {/* then delegates click events to the hidden native control. */}
            {/* Also, input and canvas require the styles to be inlined, or at least, */}
            {/* they don't work with Aphrodite StyleSheet. */}
            <input
                type="file"
                id="attach-input"
                accept={ALLOWED_FILE_TYPES.join(", ")}
                style={{display: "none"}}
            />
            <label htmlFor="attach-input">
                <Button
                    disabled={!!image}
                    kind="tertiary"
                    onClick={handleAttachClick}
                    aria-label={t`Choose an image to attach`}
                >
                    {t`Choose file`}
                </Button>
            </label>
            <canvas
                id="attach-render-canvas"
                width={`${OPENAI_MAX_SHORT_DIMENSION}px`}
                height={`${OPENAI_MAX_LONG_DIMENSION}px`}
                style={{
                    // Render offscreen
                    position: "absolute",
                    top: -10000,
                    insetInlineStart: -10000,
                }}
            />
        </>
    );
}
